import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Palette, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { IMAGE_STYLES, IMAGE_STYLE_TRANSLATIONS } from "@/lib/constants/imageStyles";

/**
 * ImageStyleSelector Component
 *
 * Displays a dropdown menu with a grid of artistic style options for image generation.
 * When a style is selected, its descriptive prompt is passed to the parent component
 * to be prepended to the user's message.
 *
 * @component
 * @example
 * <ImageStyleSelector
 *   onStyleSelection={(toolId, data) => handleStyleSelection(toolId, data)}
 *   safeT={(key, fallback) => t(key, fallback)}
 * />
 */
const ImageStyleSelector = React.forwardRef(
  ({ onStyleSelection, safeT }, ref) => {
    const [open, setOpen] = useState(false);
    const [selectedStyle, setSelectedStyle] = useState(null);

    /**
     * Translates a key to its localized string
     * Falls back to IMAGE_STYLE_TRANSLATIONS if safeT doesn't return a valid value
     */
    const translate = useCallback(
      (key, fallback) => {
        if (safeT) {
          const translated = safeT(key, fallback);
          if (translated && translated !== key) {
            return translated;
          }
        }
        return IMAGE_STYLE_TRANSLATIONS[key] || fallback || key;
      },
      [safeT]
    );

    /**
     * Handles style selection
     * Closes dropdown and calls parent callback with style data
     */
    const handleStyleSelect = useCallback(
      (style) => {
        setSelectedStyle(style);
        setOpen(false);
        if (onStyleSelection) {
          onStyleSelection("image_gen", {
            style: style.value,
            stylePrompt: style.prompt,
          });
        }
      },
      [onStyleSelection]
    );

    /**
     * Handles image load errors by displaying a gradient fallback
     */
    const handleImageError = useCallback(
      (event, style) => {
        const img = event.target;
        const container = img.parentNode;

        // Hide the broken image
        img.style.display = "none";

        // Apply gradient background
        container.style.background =
          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";

        // Create fallback text
        const fallbackText = document.createElement("div");
        fallbackText.className =
          "w-full h-full flex items-center justify-center text-white text-xs font-medium text-center px-1";
        fallbackText.textContent = translate(style.nameKey, style.nameKey);

        container.appendChild(fallbackText);
      },
      [translate]
    );

    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild ref={ref}>
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-1.5 h-8 px-2 bg-transparent hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-300 rounded-md cursor-pointer transition-all duration-200"
          >
            <Palette className="!h-4 !w-4 text-zinc-700 dark:text-zinc-300" />
            <span className="hidden sm:inline text-sm font-medium text-zinc-700 dark:text-zinc-300">
              Styles
            </span>
            <ChevronDown className="h-2.5 w-2.5 text-zinc-700 dark:text-zinc-300 ml-1" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          className="w-80 p-4 rounded-2xl z-[9999] bg-white/70 dark:bg-zinc-900/65 border border-gray-200/70 dark:border-zinc-700/70 backdrop-blur-md shadow-lg shadow-black/10"
          align="end"
          side="bottom"
          sideOffset={8}
          style={{ zIndex: 9999 }}
        >
          <div className="max-h-[240px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
            <div className="grid grid-cols-3 gap-4">
              {IMAGE_STYLES.map((style) => (
                <div
                  key={style.value}
                  className="flex flex-col items-center gap-2 cursor-pointer group"
                  onClick={() => handleStyleSelect(style)}
                >
                  {/* Circular thumbnail */}
                  <div
                    className="w-16 h-16 rounded-full overflow-hidden transition-all duration-200 hover:scale-110 focus:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 group-hover:shadow-lg border-2 border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500"
                    title={translate(style.nameKey, style.nameKey)}
                  >
                    <img
                      src={style.imageUrl}
                      alt={translate(style.nameKey, style.nameKey)}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => handleImageError(e, style)}
                    />
                  </div>

                  {/* Style name */}
                  <span className="text-xs font-medium text-gray-800 dark:text-white text-center max-w-16 leading-tight">
                    {translate(style.nameKey, style.nameKey)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
);

ImageStyleSelector.displayName = "ImageStyleSelector";

ImageStyleSelector.propTypes = {
  /**
   * Callback function when a style is selected
   * @param {string} toolId - The tool identifier ("image_gen")
   * @param {Object} data - Style data including value and stylePrompt
   */
  onStyleSelection: PropTypes.func,

  /**
   * Translation function for i18n support
   * @param {string} key - Translation key
   * @param {string} fallback - Fallback text if translation not found
   * @returns {string} Translated text
   */
  safeT: PropTypes.func,
};

export default ImageStyleSelector;
